import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { DEFAULT_OG_IMAGE, SITE_NAME, SITE_URL, absoluteUrl } from '../../utils/seo';

interface SeoProps {
  title: string;
  description: string;
  canonical?: string;
  image?: string;
  type?: 'website' | 'article';
  noindex?: boolean;
  keywords?: string;
  jsonLd?: object | object[];
  publishedTime?: string;
}

const upsertMeta = (attr: 'name' | 'property', key: string, content: string) => {
  let element = document.head.querySelector(`meta[${attr}="${key}"]`) as HTMLMetaElement | null;
  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attr, key);
    document.head.appendChild(element);
  }
  element.setAttribute('content', content);
};

const upsertLink = (rel: string, href: string) => {
  let element = document.head.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;
  if (!element) {
    element = document.createElement('link');
    element.setAttribute('rel', rel);
    document.head.appendChild(element);
  }
  element.setAttribute('href', href);
};

const JSON_LD_ID = 'koostream-jsonld';

export default function Seo({
  title,
  description,
  canonical,
  image,
  type = 'website',
  noindex = false,
  keywords,
  jsonLd,
  publishedTime,
}: SeoProps) {
  const location = useLocation();
  const canonicalUrl = canonical || absoluteUrl(location.pathname + location.search);
  const ogImage = image || DEFAULT_OG_IMAGE;
  const fullTitle = title.includes(SITE_NAME) ? title : `${title} | ${SITE_NAME}`;

  const jsonLdText = jsonLd ? JSON.stringify(jsonLd) : '';

  useEffect(() => {
    document.title = fullTitle;

    upsertMeta('name', 'description', description);
    if (keywords) {
      upsertMeta('name', 'keywords', keywords);
    }
    upsertMeta('name', 'robots', noindex ? 'noindex, nofollow' : 'index, follow, max-image-preview:large');
    upsertMeta('name', 'author', `${SITE_NAME} Editorial`);
    upsertMeta('name', 'theme-color', '#111111');

    upsertMeta('property', 'og:type', type);
    upsertMeta('property', 'og:site_name', SITE_NAME);
    upsertMeta('property', 'og:title', fullTitle);
    upsertMeta('property', 'og:description', description);
    upsertMeta('property', 'og:url', canonicalUrl);
    upsertMeta('property', 'og:image', ogImage);
    upsertMeta('property', 'og:locale', 'en_US');

    upsertMeta('name', 'twitter:card', 'summary_large_image');
    upsertMeta('name', 'twitter:title', fullTitle);
    upsertMeta('name', 'twitter:description', description);
    upsertMeta('name', 'twitter:image', ogImage);

    if (publishedTime) {
      upsertMeta('property', 'article:published_time', publishedTime);
    }

    upsertLink('canonical', canonicalUrl);

    const existing = document.getElementById(JSON_LD_ID);
    if (existing) {
      existing.remove();
    }
    if (jsonLdText) {
      const script = document.createElement('script');
      script.id = JSON_LD_ID;
      script.type = 'application/ld+json';
      script.text = jsonLdText;
      document.head.appendChild(script);
    }

    window.scrollTo(0, 0);
  }, [fullTitle, description, canonicalUrl, ogImage, type, noindex, keywords, jsonLdText, publishedTime]);

  return null;
}

export { SITE_URL };
